import {
  createContext, useContext, useMemo, useState,
} from 'react';

import type { ReactNode } from 'react';

interface IDockRightSlotContextValue {
  slot: HTMLDivElement | null,
  setSlot: (node: HTMLDivElement | null) => void,
}

const DockRightSlotContext = createContext<IDockRightSlotContextValue>({
  slot: null,
  setSlot: () => {},
});

/**
 * Holds the DOM node any panel docked to the right portals into.
 *
 * Wraps the page layout; `DockRightSlot` renders the node itself wherever the layout wants the
 * free space split, so a dockable panel never has to know where that is, only that it exists.
 */
function DockRightSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const value = useMemo<IDockRightSlotContextValue>(() => ({ slot, setSlot }), [slot]);

  return (
    <DockRightSlotContext.Provider value={value}>
      {children}
    </DockRightSlotContext.Provider>
  );
}

/** The slot's DOM node once `DockRightSlot` has mounted, or null while there is none to dock into. */
export const useDockRightSlot = () => useContext(DockRightSlotContext).slot;

/** What `DockRightSlot` calls to publish (or clear, on unmount) the node it rendered. */
export const useRegisterDockRightSlot = () => useContext(DockRightSlotContext).setSlot;

export default DockRightSlotProvider;
